// lib/taskPatch.mjs — R115 fix (review tasks 0): the Tasks drawer sends ONLY
// what the user changed, and folds a newer server copy of the task into an
// open draft without losing either side's edits.
//
// Why: the page used to POST its whole copy of a task (loaded when the page
// rendered) on every save, row-select status flip, board drag and checklist
// tick. The server applies every key it receives, so an old copy reverted
// other people's newer edits — and since R115 C1/C2 those reverts were logged
// under the saver's name and sent out as notifications ("removed you from
// this task"). Now:
//   patchBody(draft, base)   → { id, ...changed keys } or null (nothing to send)
//   mergeDraft(local, base, fresh) → the draft with `fresh` (the server's
//                              newer task) folded in: keys the user did not
//                              touch take the server's value; keys they did
//                              keep theirs; people lists and the checklist
//                              merge both sides' changes.
// Shared by client/src/pages/TasksPage.ts (types: lib/taskPatch.d.mts) and
// tested by `node lib/taskPatch.test.mjs` (pure, no I/O).

// The keys a save may carry — the server's field set (server/tasks.js POST /).
// Everything else (comments, log, created_*, completed_at, updated_at,
// date_alert, source_ref) is server-managed and never sent.
export const EDITABLE = ['title', 'description', 'status', 'priority', 'assignees', 'assignee', 'watchers', 'due_date', 'due_time', 'listing_id', 'listing_name', 'link', 'checklist', 'blocked_by', 'recurrence', 'template_id'];
// Keys that travel together: the assignee mirror follows assignees[]; a
// property pick sets id + name as one.
const GROUPS = [['assignees', 'assignee'], ['listing_id', 'listing_name']];
const groupOf = (k) => GROUPS.find(g => g.includes(k)) || [k];
// Whole-array fields: sending one rewrites the whole list, so the body also
// carries base_updated_at and the server answers 409 when the task moved on.
export const GUARDED = ['checklist', 'assignees', 'watchers'];

// undefined ≡ null (a legacy row lacks keys a draft input later sets to null).
const J = (v) => JSON.stringify(v === undefined ? null : v);
const clone = (v) => (v === undefined ? undefined : JSON.parse(JSON.stringify(v)));
const lower = (v) => String(v == null ? '' : v).trim().toLowerCase();

// The editable keys whose value differs between draft and base, groups expanded.
export function changedKeys(draft, base) {
  const d = draft || {}, b = base || {};
  const out = new Set();
  for (const k of EDITABLE) {
    if (J(d[k]) === J(b[k])) continue;
    for (const g of groupOf(k)) out.add(g);
  }
  return EDITABLE.filter(k => out.has(k));
}

// The POST body for a drawer save: { id, ...changed }, plus base_updated_at
// when a whole-array field is in it. null when nothing changed.
// R115 fix 3: `force` = keys to send even when draft and base agree, and
// `guard` = always send base_updated_at — for a task whose last save's
// outcome is unknown (a dropped connection: it may have landed). Guarded, the
// body only lands on exactly `base` (the server answers 409 if anything —
// that save included — moved it), so writing those keys can't revert anyone.
export function patchBody(draft, base, force = [], guard = false) {
  if (!draft || !draft.id) return null;
  const set = new Set(changedKeys(draft, base));
  for (const k of force || []) if (EDITABLE.includes(k)) for (const g of groupOf(k)) set.add(g);
  const keys = EDITABLE.filter(k => set.has(k));
  if (!keys.length) return null;
  const body = { id: draft.id };
  for (const k of keys) body[k] = draft[k] === undefined ? null : clone(draft[k]);
  if ((guard || keys.some(k => GUARDED.includes(k))) && base && base.updated_at) body.base_updated_at = base.updated_at;
  return body;
}

// People lists: apply this side's adds/removes (base → local) to the server's list.
function mergePeople(local, base, fresh) {
  const arr = (v) => (Array.isArray(v) ? v.map(x => String(x).trim()).filter(Boolean) : []);
  const L = arr(local), B = arr(base), F = arr(fresh);
  const lset = new Set(L.map(lower)), bset = new Set(B.map(lower));
  const removed = new Set(B.filter(x => !lset.has(lower(x))).map(lower));
  const added = L.filter(x => !bset.has(lower(x)));
  const out = F.filter(x => !removed.has(lower(x)));
  const have = new Set(out.map(lower));
  for (const a of added) if (!have.has(lower(a))) { out.push(a); have.add(lower(a)); }
  return out;
}
// Checklist: item by item (key). An item this side edited keeps this side's
// version, one it deleted stays deleted, one it added is appended; every other
// item is the server's. Keyless items (legacy) can't be matched — this side's
// whole list wins then, as before.
function mergeChecklist(local, base, fresh) {
  const L = Array.isArray(local) ? local : [], B = Array.isArray(base) ? base : [], F = Array.isArray(fresh) ? fresh : [];
  const keyed = (list) => list.every(it => it && typeof it === 'object' && it.key);
  if (!keyed(L) || !keyed(B) || !keyed(F)) return clone(L);
  const lmap = new Map(L.map(it => [it.key, it])), bmap = new Map(B.map(it => [it.key, it]));
  const out = [];
  for (const it of F) {
    const mine = lmap.get(it.key), was = bmap.get(it.key);
    if (was && !mine) continue;                                    // deleted here
    out.push(clone(mine && was && J(mine) !== J(was) ? mine : it)); // edited here → ours
  }
  const have = new Set(out.map(it => it.key));
  for (const it of L) if (!bmap.has(it.key) && !have.has(it.key)) out.push(clone(it));   // added here
  return out;
}

// Fold the server's newer copy (fresh) into the open draft (local), given the
// copy the draft started from (base). Returns a new object shaped like fresh.
export function mergeDraft(local, base, fresh) {
  const out = clone(fresh || {});
  const l = local || {}, b = base || {}, f = fresh || {};
  const done = new Set();
  for (const k of EDITABLE) {
    if (done.has(k)) continue;
    const group = groupOf(k);
    group.forEach(g => done.add(g));
    const mine = J(group.map(g => l[g])), was = J(group.map(g => b[g])), theirs = J(group.map(g => f[g]));
    if (mine === was) continue;                                   // untouched here → the server's
    const keepMine = () => { for (const g of group) { if (l[g] === undefined) delete out[g]; else out[g] = clone(l[g]); } };
    if (theirs === was || theirs === mine) { keepMine(); continue; }   // only this side changed it
    // both sides changed it
    if (k === 'assignees') {
      const people = (t) => (Array.isArray(t.assignees) && t.assignees.length ? t.assignees : (t.assignee ? [t.assignee] : []));
      out.assignees = mergePeople(people(l), people(b), people(f));
      out.assignee = out.assignees[0] || null;
    } else if (k === 'watchers') {
      out.watchers = mergePeople(l.watchers, b.watchers, f.watchers);
    } else if (k === 'checklist') {
      out.checklist = mergeChecklist(l.checklist, b.checklist, f.checklist);
    } else {
      keepMine();                                                 // a value typed in the form wins on Save
    }
  }
  return out;
}

// ── The drawer's save queue (R115 fix 3, verifier D0) ──────────────────────
// A drawer save is captured when it is QUEUED as
//   { want0, base0, dep, landed }
//   want0  the draft as the user left it;
//   base0  what the server will hold once every save still ahead of it (dep,
//          dep.dep, …) lands — built ON THE SAME COPY of the task the draft is
//          layered on (the panel's base), so want0 vs base0 is exactly this
//          save's own edits: an undo of a still-pending change is an edit, and
//          a value another user set (folded into the panel) never is;
//   dep    the save queued just before it on the same task + panel opening
//          while that one was still pending (null when there was none);
//   landed the server took it (or there was nothing to send).
// When a save runs it is REPLAYED onto the newest server copy: first the own
// edits of every save ahead of it that did NOT land (it carries them — the
// user was not told about those failures), oldest first, then its own. A save
// that landed is in every newer server copy and is never replayed (replaying
// an old value would undo someone else's newer edit). Never diff a draft
// against a copy it was not layered on: that is how a stale priority / due
// date gets sent back as if the user had typed it.
export function applyEdits(save, onto) {
  return mergeDraft(save.want0, save.base0, onto);
}
// The state `save`'s own edits go on top of: `server` + the edits of the
// not-landed saves ahead of it (all of them while they are pending — at
// queue time; only the failed ones once they have run).
export function stateBefore(save, server) {
  const carry = [];
  for (let d = save && save.dep; d && !d.landed; d = d.dep) carry.push(d);
  let s = server;
  for (let i = carry.length - 1; i >= 0; i--) s = applyEdits(carry[i], s);
  return s;
}
// What `save` sends when `server` is the newest copy known: patchBody(replaySave(save, server), server).
export function replaySave(save, server) {
  return applyEdits(save, stateBefore(save, server));
}
